import type { CoreLocation } from './useCoreLocations';

export interface AccountLocationRef {
  id: string;
  name: string;
  ascend_id: string | null;
}

export interface ResolveCoreLocationOptions {
  /** Scoped location key from `access`, when it differs from `locations[].ascend_id`. */
  accessLocationAscendId?: string | null;
  /** How many locations the signed-in session exposes in the picker. */
  accountLocationCount?: number;
}

function normalizeName(value: string): string {
  return value.trim().toLowerCase();
}

function uniqueAscendCandidates(
  accountLocation: AccountLocationRef,
  accessLocationAscendId?: string | null
): string[] {
  const seen = new Set<string>();
  const candidates: string[] = [];

  for (const value of [accountLocation.ascend_id, accessLocationAscendId]) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    candidates.push(value);
  }

  return candidates;
}

/**
 * Maps an account location UUID to a core-api-az row.
 * Primary key: practice-management id (`ascend_id` / `external_id`).
 * Fallbacks cover single-location orgs and name matches when account data is incomplete.
 */
export function matchCoreLocation(
  accountLocation: AccountLocationRef,
  coreLocations: CoreLocation[],
  options: ResolveCoreLocationOptions = {}
): CoreLocation | undefined {
  if (!coreLocations.length) return undefined;

  for (const candidate of uniqueAscendCandidates(accountLocation, options.accessLocationAscendId)) {
    const byExternalId = coreLocations.find((core) => core.external_id === candidate);
    if (byExternalId) return byExternalId;
  }

  const normalizedAccountName = normalizeName(accountLocation.name);
  if (normalizedAccountName) {
    const byName = coreLocations.find((core) => normalizeName(core.name) === normalizedAccountName);
    if (byName) return byName;
  }

  const accountCount = options.accountLocationCount ?? 1;
  if (accountCount === 1 && coreLocations.length === 1) {
    return coreLocations[0];
  }

  return undefined;
}

export function resolveCoreLocationNumericId(
  accountLocation: AccountLocationRef,
  coreLocations: CoreLocation[],
  options?: ResolveCoreLocationOptions
): number | undefined {
  return matchCoreLocation(accountLocation, coreLocations, options)?.id;
}
